/**
 * Asset Custom PLX Tag Component
 * 
 * Generates and displays custom 7x7 binary PLX tags for assets (AEGIS LITE 7x7)
 * Uses 49-bit payload with anchors, ID, and safety checksum
 */

/* eslint-env es2020 */

import React, { useRef, useMemo } from 'react';
import { Download, X } from 'lucide-react';

/**
 * Generate custom 7x7 PLX tag SVG from asset ID (AEGIS LITE 7x7 format)
 * @param {string} assetId - The asset ID (e.g., "G2-1000")
 * @returns {object} { svg: string, assetIdValue: number, safety: number, anchors: number, payload: BigInt }
 */
function generatePLXTag(assetId) {
  // Extract numeric portion from asset ID
  const numericMatch = assetId.match(/\d+/);
  if (!numericMatch) {
    throw new Error('Asset ID must contain a numeric value');
  }
  
  // Convert ID to BigInt for safety
  const idBig = BigInt(numericMatch[0]);
  const assetIdValue = Number(idBig);
  
  // Validate asset ID is within 37-bit range (0 to 137,438,953,471)
  if (idBig === 0n || idBig > 0x1FFFFFFFFFn) {
    throw new Error('Asset ID must be between 1 and 137,438,953,471');
  }
  
  // Calculate Safety (8 Bits)
  const safety = Number(idBig % 255n);
  
  // Create Anchor Bar (4 Bits) -> 1111 (Decimal 15)
  // These are at the very end of the 49-bit string (Bits 45,46,47,48)
  const anchors = 15n;
  
  // Combine Payload
  // [ANCHORS (4)] + [ID (37)] + [SAFETY (8)]
  // Shift Anchors up 45 spots (8 + 37)
  // Shift ID up 8 spots
  const payload = (anchors << 45n) | (idBig << 8n) | BigInt(safety);
  
  // Generate 7x7 binary grid from payload
  const grid = generateGridFromPayload(payload);
  
  // Grid parameters (9x9 total: 1-cell border + 7x7 data)
  const gridSize = 7;
  const borderSize = 1;
  const cellSize = 50;
  const totalSize = (gridSize + borderSize * 2) * cellSize;
  const padding = cellSize;
  const canvasSize = totalSize + (padding * 2);
  
  // Generate SVG with proper viewBox for scaling (responsive)
  let svg = `<svg viewBox="0 0 ${canvasSize} ${canvasSize}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" style="width: 100%; height: 100%;">`;
  svg += `<rect x="0" y="0" width="${canvasSize}" height="${canvasSize}" fill="white"/>`;
  
  // The Black Background Border
  svg += `<rect x="${padding}" y="${padding}" width="${totalSize}" height="${totalSize}" fill="black"/>`;
  
  // The Grid Area
  const innerOffset = padding + (borderSize * cellSize);
  
  // Draw 7x7 inner data grid (white cells = 1, black cells = 0)
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const bitValue = grid[row][col];
      if (bitValue === 1) {
        // White cell (value = 1) - draw white rectangle
        const x = innerOffset + (col * cellSize);
        const y = innerOffset + (row * cellSize);
        svg += `<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" fill="#fff"/>`;
      }
      // Black cells (value = 0) - no need to draw, background is already black
    }
  }
  
  svg += `</svg>`;

  return { svg, assetIdValue, safety, anchors: Number(anchors), payload };
}

/**
 * Convert 49-bit payload to 7x7 binary grid
 * Traverses left-to-right, top-to-bottom (all 49 bits)
 * @param {BigInt} payload - 49-bit integer payload
 * @returns {number[][]} 7x7 bit array
 */
function generateGridFromPayload(payload) {
  const grid = Array(7).fill(0).map(() => Array(7).fill(0));
  
  let bitIndex = 0n;
  for (let row = 0; row < 7; row++) {
    for (let col = 0; col < 7; col++) {
      // Extract bit at position bitIndex
      grid[row][col] = Number((payload >> bitIndex) & 1n);
      bitIndex++;
    }
  }
  
  return grid;
}

export default function AssetQRCode({ asset, isDark, onClose, onCancel }) {
  const tagRef = useRef(null);

  // Generate custom 7x7 PLX tag SVG (AEGIS LITE 7x7)
  const markerData = useMemo(() => {
    try {
      // Use fiducial_marker_id if available (for expendable assets), otherwise use asset.id
      const markerId = asset.fiducial_marker_id ? String(asset.fiducial_marker_id) : asset.id;
      return generatePLXTag(markerId);
    } catch (error) {
      console.error('Error generating PLX tag:', error);
      return null;
    }
  }, [asset.id, asset.fiducial_marker_id]);
  
  const tagSvg = markerData?.svg;

  const downloadTag = () => {
    if (!tagSvg) return;

    // Convert SVG to PNG via canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    const svgBlob = new Blob([tagSvg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    
    img.onload = () => {
      canvas.width = 256;
      canvas.height = 256;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 256, 256);
      ctx.drawImage(img, 0, 0, 256, 256);
      
      const pngUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `plx-tag-${asset.id}.png`;
      link.href = pngUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };
    
    img.src = url;
  };

  // Helper function to get current date/time in Central Time
  const getCentralTimeString = () => {
    const now = new Date();
    // Convert to Central Time (UTC-6 for CST, UTC-5 for CDT)
    // JavaScript's toLocaleString handles DST automatically
    const centralTime = now.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    return centralTime;
  };

  const printTag = () => {
    if (!tagSvg) return;

    // For expendable assets, use item_id if available, otherwise use asset.id
    const displayId = (asset.category === 'Expendable' && asset.item_id) ? asset.item_id : asset.id;
    const printTimestamp = getCentralTimeString();

    const printWindow = window.open('', '', 'width=600,height=800');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Asset PLX Tag - ${asset.id}</title>
          <style>
            @page {
              size: 4in 6in;
              margin: 0;
            }
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 0;
              margin: 0;
              width: 4in;
              height: 6in;
              font-family: Arial, sans-serif;
            }
            .tag-container {
              text-align: center;
              border: 2px solid #000;
              padding: 0.2in;
              width: 3.7in;
              box-sizing: border-box;
            }
            h2 {
              font-size: 16px;
              margin: 0 0 0.15in 0;
            }
            .tag-image {
              margin: 0.15in 0;
              width: 2.5in;
              height: 2.5in;
            }
            .info {
              margin: 0.08in 0;
              font-size: 11px;
              text-align: left;
            }
            .info strong {
              display: inline-block;
              width: 1.2in;
              text-align: right;
              margin-right: 0.1in;
            }
            @media print {
              body {
                margin: 0;
              }
            }
          </style>
        </head>
        <body>
            <div class="tag-container">
            <h2>Asset PLX Tag (7x7)</h2>
            <div class="tag-image">${tagSvg}</div>
            <div class="info"><strong>Asset Name:</strong> ${asset.name || asset.id}</div>
            <div class="info"><strong>Asset ID:</strong> ${displayId}</div>
            ${asset.fiducial_marker_id ? `<div class="info"><strong>PLX Tag ID:</strong> ${asset.fiducial_marker_id}</div>` : ''}
            <div class="info"><strong>Type:</strong> ${asset.assetType || asset.type || 'N/A'}</div>
            <div class="info"><strong>Date:</strong> ${printTimestamp} CT</div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const printTagOnly = () => {
    if (!tagSvg) return;

    const printTimestamp = getCentralTimeString();
    const printWindow = window.open('', '', 'width=600,height=800');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>PLX Tag - ${asset.fiducial_marker_id || asset.id}</title>
          <style>
            @page {
              size: 4in 6in;
              margin: 0;
            }
            body {
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 0;
              margin: 0;
              width: 4in;
              height: 6in;
              font-family: Arial, sans-serif;
            }
            .tag-container {
              text-align: center;
              width: 100%;
              height: 100%;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
            }
            .tag-image {
              width: 3.5in;
              height: 3.5in;
            }
            .print-timestamp {
              font-size: 10px;
              color: #666;
              margin-top: 0.1in;
              text-align: center;
            }
            @media print {
              body {
                margin: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="tag-container">
            <div class="tag-image">${tagSvg}</div>
            <div class="print-timestamp">Date: ${printTimestamp} CT</div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const printTagWithName = () => {
    if (!tagSvg) return;

    const printTimestamp = getCentralTimeString();
    const printWindow = window.open('', '', 'width=600,height=800');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>PLX Tag - ${asset.name || asset.id}</title>
          <style>
            @page {
              size: 4in 6in;
              margin: 0;
            }
            body {
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 0;
              margin: 0;
              width: 4in;
              height: 6in;
              font-family: Arial, sans-serif;
            }
            .tag-container {
              text-align: center;
              width: 100%;
              height: 100%;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 0.25in;
              box-sizing: border-box;
            }
            .tag-image {
              width: 3in;
              height: 3in;
              margin-bottom: 0.25in;
            }
            .asset-name {
              font-size: 24px;
              font-weight: bold;
              color: #000;
              text-align: center;
              word-wrap: break-word;
              max-width: 3.5in;
            }
            .print-timestamp {
              font-size: 10px;
              color: #666;
              margin-top: 0.1in;
              text-align: center;
            }
            @media print {
              body {
                margin: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="tag-container">
            <div class="tag-image">${tagSvg}</div>
            <div class="asset-name">${asset.name || asset.id}</div>
            <div class="print-timestamp">Date: ${printTimestamp} CT</div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  if (!markerData || !tagSvg) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className={`border rounded-xl w-full max-w-md shadow-2xl p-6 ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
          <p className={isDark ? 'text-white' : 'text-gray-900'}>Error: Unable to generate PLX tag for asset ID: {asset.id}</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`border rounded-xl w-full max-w-md shadow-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
          <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Asset PLX Tag (7x7)</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded-lg transition-colors">
            <X size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
          </button>
        </div>

        {/* PLX Tag */}
        <div className="p-6 space-y-4">
          <div ref={tagRef} className={`flex justify-center p-6 rounded-lg ${isDark ? 'bg-white' : 'bg-gray-50'}`}>
            <div 
              style={{ 
                width: '256px', 
                height: '256px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}
              dangerouslySetInnerHTML={{ __html: tagSvg }}
            />
          </div>

          {/* Asset Info */}
          <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Asset Name:</span>
                <span className={`font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>{asset.name || asset.id}</span>
              </div>
              <div className="flex justify-between">
                <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Asset ID:</span>
                <span className={`font-mono font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
                  {(asset.category === 'Expendable' && asset.item_id) ? asset.item_id : asset.id}
                </span>
              </div>
              {asset.fiducial_marker_id && (
                <div className="flex justify-between">
                  <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>PLX Tag ID:</span>
                  <span className={`font-mono font-medium text-blue-500`}>{asset.fiducial_marker_id}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Type:</span>
                <span className={`font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>{asset.assetType || asset.type || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={printTagOnly}
                className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors text-sm"
              >
                Print Tag Only
              </button>
              <button
                onClick={printTagWithName}
                className="px-4 py-2.5 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium transition-colors text-sm"
              >
                Print Tag + Name
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={downloadTag}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors text-sm ${
                  isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                <Download size={16} />
                Download
              </button>
              <button
                onClick={printTag}
                className={`px-4 py-2.5 rounded-lg font-medium transition-colors text-sm ${
                  isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                Print All Info
              </button>
            </div>
            {/* Cancel button for expendable assets with newly assigned markers */}
            {onCancel && asset.category === 'Expendable' && asset.fiducial_marker_id && (
              <button
                onClick={onCancel}
                className={`w-full px-4 py-2.5 rounded-lg font-medium transition-colors text-sm border ${
                  isDark 
                    ? 'bg-transparent border-red-600 text-red-400 hover:bg-red-600/20' 
                    : 'bg-transparent border-red-500 text-red-600 hover:bg-red-50'
                }`}
              >
                Cancel & Release Marker
              </button>
            )}
          </div>

          <p className={`text-xs text-center ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
            Scan this 7x7 binary PLX tag to identify the asset (49-bit payload: Anchors + ID + Safety)
          </p>
        </div>
      </div>
    </div>
  );
}
